import { FC, isValidElement, cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { Box, FormLabel, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useGoogleMaps } from '../../../../../hooks/useGoogleMaps';
import { usePacContainerWidthSync } from '../../../../../hooks/usePacContainerWidthSync';
import { extractCountryAndCityFromPlace } from '../../../../../utils/googlePlaceCityCountry';

import styles from './HotelLocationField.module.scss';
import '../../../../../common/GooglePlacesInput/pacDropdown.module.scss';
import iconStyles from '../discoverFieldIcons.module.scss';
import { LocationIcon } from '../../../../../components/Icons/LocationIcon';

export interface HotelLocationPlaceDetails {
  formattedAddress: string;
  name?: string;
  countryCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  raw: google.maps.places.PlaceResult;
}

type HotelLocationFieldProps = {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  inputLabel?: string;
  placeholder?: string;
  hideStartIcon?: boolean;
  discoverChrome?: boolean;
  startAdornment?: React.ReactNode;
  compactForMobile?: boolean;
  allowClear?: boolean;
  disableGooglePlaces?: boolean;
  placeTypes?: string[];
  onPlaceSelected?: (details: HotelLocationPlaceDetails | null) => void;
};

const HotelLocationField: FC<HotelLocationFieldProps> = ({
  name,
  value,
  onChange,
  error,
  inputLabel = 'Location',
  placeholder = 'City or address',
  hideStartIcon = false,
  discoverChrome = false,
  startAdornment,
  compactForMobile = false,
  allowClear = false,
  disableGooglePlaces = false,
  placeTypes,
  onPlaceSelected,
}) => {
  const { isLoaded } = useGoogleMaps();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const isUpdatingFromGoogleRef = useRef(false);
  const isFieldActiveRef = useRef(false);
  const [hasSelectedPlace, setHasSelectedPlace] = useState(false);
  const [pacSyncEnabled, setPacSyncEnabled] = useState(false);

  usePacContainerWidthSync(inputRef, isLoaded && !disableGooglePlaces && pacSyncEnabled);

  const withIconClass = (node: React.ReactNode) => {
    if (isValidElement<{ className?: string }>(node)) {
      const { className: existingClass } = node.props;
      return cloneElement(node, {
        className: [iconStyles.svgIcon, existingClass].filter(Boolean).join(' '),
      });
    }
    return node;
  };

  const emitPlaceFromAutocomplete = useCallback(() => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place) return;

    const formatted = place.formatted_address || place.name || '';
    if (!formatted) return;

    // Ignore stale/incomplete getPlace() (e.g. Enter without a pick, or a race before Google fills the result).
    if (!place.place_id || !place.address_components?.length) return;

    const { countryCode, city } = extractCountryAndCityFromPlace(place);

    const latitude = place.geometry?.location?.lat();
    const longitude = place.geometry?.location?.lng();

    isUpdatingFromGoogleRef.current = true;
    onChange({
      target: { name, value: formatted },
    } as React.ChangeEvent<HTMLInputElement>);

    onPlaceSelected?.({
      formattedAddress: formatted,
      name: place.name,
      countryCode,
      city,
      latitude,
      longitude,
      placeId: place.place_id,
      raw: place,
    });
    setHasSelectedPlace(true);

    setTimeout(() => {
      isUpdatingFromGoogleRef.current = false;
    }, 100);
  }, [name, onChange, onPlaceSelected]);

  useEffect(() => {
    if (disableGooglePlaces) return;
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    try {
      const options: google.maps.places.AutocompleteOptions = {
        types: placeTypes ?? ['geocode'],
        fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'name', 'types'],
      };

      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
      autocompleteRef.current.addListener('place_changed', emitPlaceFromAutocomplete);
    } catch (err) {
      console.error('Failed to initialize Google Places Autocomplete:', err);
    }
  }, [isLoaded, disableGooglePlaces, placeTypes, emitPlaceFromAutocomplete]);

  useEffect(() => {
    if (disableGooglePlaces) return;
    const handleDocumentClick = (e: MouseEvent) => {
      if (!isFieldActiveRef.current) return;
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer?.contains(e.target as Node)) {
        setTimeout(() => {
          emitPlaceFromAutocomplete();
        }, 300);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [disableGooglePlaces, emitPlaceFromAutocomplete]);

  const handleFocus = () => {
    isFieldActiveRef.current = true;
    setPacSyncEnabled(true);
  };

  const handleBlur = () => {
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        const input = inputRef.current;
        if (!input) {
          isFieldActiveRef.current = false;
          setPacSyncEnabled(false);
          return;
        }
        const active = document.activeElement;
        const outlineRoot = input.closest('.MuiOutlinedInput-root');
        const pac = document.querySelector('.pac-container');
        const stillInField = active === input || Boolean(outlineRoot?.contains(active));
        const inPacList = Boolean(pac?.contains(active));
        if (stillInField || inPacList) return;
        isFieldActiveRef.current = false;
        setPacSyncEnabled(false);
      }, 0);
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event);
    if (isUpdatingFromGoogleRef.current) return;
    if (hasSelectedPlace) {
      setHasSelectedPlace(false);
      onPlaceSelected?.(null);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disableGooglePlaces) return;
    if (event.key === 'Enter') {
      const hasDropdown = document.querySelector('.pac-container:not(.pac-placeholder)');
      if (hasDropdown) {
        event.preventDefault();
      }
    }
  };

  const lead =
    startAdornment != null ? (
      <InputAdornment
        position="start"
        className={iconStyles.adornmentTight}
      >
        {withIconClass(startAdornment)}
      </InputAdornment>
    ) : discoverChrome ? (
      <InputAdornment
        position="start"
        className={iconStyles.adornmentTight}
      >
        <LocationIcon
          size={20}
          className={iconStyles.svgIcon}
        />
      </InputAdornment>
    ) : !hideStartIcon ? (
      <InputAdornment
        position="start"
        className={iconStyles.adornmentTight}
      >
        <LocationIcon
          size={20}
          className={styles.leadIcon}
        />
      </InputAdornment>
    ) : null;

  const hasLead = Boolean(lead);
  const showClear = allowClear && Boolean(value.trim());
  const fieldClass = `${styles.textField} ${!hasLead ? styles.textFieldNoIcon : ''}`.trim();

  const endClear = showClear ? (
    <InputAdornment position="end">
      <IconButton
        type="button"
        size="small"
        aria-label="Clear"
        edge="end"
        onMouseDown={e => e.preventDefault()}
        onClick={() => {
          onChange({
            target: { name, value: '' },
          } as React.ChangeEvent<HTMLInputElement>);
          if (hasSelectedPlace) {
            setHasSelectedPlace(false);
            onPlaceSelected?.(null);
          }
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </InputAdornment>
  ) : undefined;

  const inputProps: { startAdornment?: typeof lead; endAdornment?: typeof endClear } = {};
  if (hasLead) inputProps.startAdornment = lead;
  if (endClear) inputProps.endAdornment = endClear;

  return (
    <Box
      className={styles.root}
      data-compact-mobile={compactForMobile ? 'true' : undefined}
    >
      <FormLabel className={styles.label}>
        <Typography
          variant="h11Semibold"
          color="#222B3A"
        >
          {inputLabel}
        </Typography>
      </FormLabel>
      <TextField
        name={name}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        error={!!error}
        fullWidth
        variant="outlined"
        className={`${fieldClass}${showClear ? ` ${styles.textFieldWithClear}` : ''}`.trim()}
        InputProps={inputProps}
        inputRef={inputRef}
        autoComplete="off"
      />
      <Box
        className={styles.errorSlot}
        role={error ? 'alert' : undefined}
      >
        {error ? (
          <Typography
            variant="h12"
            className={styles.errorText}
          >
            {error}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default HotelLocationField;
